import { ActionContext, ActionTree, MutationTree, Module as VuexModuleInterface } from 'vuex'
import { sessionApi, userApi } from '@/api'
import Cookies from 'js-cookie'
import { LayoutMutationTypes } from '@/store/modules/layout'
import { RootState } from '@/store'
import { UserInterface } from '@/model/user/UserInterface'
import cookies from '@/cookies'
import draftStorage from '@/storage/DraftStorage'
import { sleep } from '@/utils'

export interface AppState {
  appIsLoaded: boolean
  currentUser: UserInterface | null
  isUserAdmin: boolean
  loading: boolean
}

export const AppMutationTypes = {
  LOADED: 'LOADED',
  LOADING: 'LOADING',
  RESET_IS_USER_ADMIN: 'RESET_IS_USER_ADMIN',
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  SET_IS_USER_ADMIN: 'SET_IS_USER_ADMIN',
  STOP_LOADING: 'STOP_LOADING'
}

const state = (): AppState => {
  return {
    appIsLoaded: Cookies.get(cookies.APP_IS_LOADED) !== 'false',
    currentUser: null,
    isUserAdmin: Cookies.get(cookies.IS_USER_ADMIN) !== 'false',
    loading: false
  }
}

const mutations: MutationTree<AppState> = {
  [AppMutationTypes.LOADED](state) {
    Cookies.set(cookies.APP_IS_LOADED, 'true')
    state.loading = false
  },
  [AppMutationTypes.LOADING](state) {
    if (Cookies.get(cookies.APP_IS_LOADED)) {
      Cookies.set(cookies.APP_IS_LOADED, 'false')
    }

    state.loading = true
  },
  [AppMutationTypes.RESET_IS_USER_ADMIN]: (state) => {
    Cookies.remove(cookies.IS_USER_ADMIN)
    state.isUserAdmin = false
  },
  [AppMutationTypes.SET_CURRENT_USER]: (state, user: UserInterface) => {
    state.currentUser = user
  },
  [AppMutationTypes.SET_IS_USER_ADMIN]: (state, isAdmin: boolean) => {
    Cookies.set(cookies.IS_USER_ADMIN, isAdmin ? 'true' : 'false')
    state.isUserAdmin = isAdmin
  },
  [AppMutationTypes.STOP_LOADING](state) {
    Cookies.remove(cookies.APP_IS_LOADED)
    state.loading = false
  }
}

interface Actions<S, R> extends ActionTree<S, R> {
  checkUserIsAdmin(context: ActionContext<S, R>): Promise<void>

  getCurrentUser(context: ActionContext<S, R>): Promise<void>

  initApp(context: ActionContext<S, R>, helloCallback: (username: string) => void): Promise<void>

  logout(context: ActionContext<S, R>): Promise<void>

  setCurrentUser(context: ActionContext<S, R>, user: UserInterface): void
}

const actions: Actions<AppState, RootState> = {
  async checkUserIsAdmin({ commit }) {
    if (!Cookies.get(cookies.IS_USER_ADMIN)) {
      const { hasPermission } = await userApi.checkPermission('ROLE_ADMIN')

      commit(AppMutationTypes.SET_IS_USER_ADMIN, hasPermission)
    }
  },
  async getCurrentUser({ commit }) {
    const user = await userApi.getCurrent()

    commit(AppMutationTypes.SET_CURRENT_USER, user)
  },
  async initApp({ commit, dispatch, state }, helloCallback: (username: string) => void) {
    if (!Cookies.get(cookies.APP_IS_LOADED) || Cookies.get(cookies.APP_IS_LOADED) === 'false') {
      commit(AppMutationTypes.LOADING)
      await dispatch('checkUserIsAdmin')
      await dispatch('getCurrentUser')
      commit(AppMutationTypes.LOADED)

      if (state.currentUser) {
        helloCallback(state.currentUser.username)
      }
    }
  },
  async logout({ commit }) {
    commit(`layout/${LayoutMutationTypes.HIDE_LAYOUT}`, null, { root: true })
    await sleep(500)
    commit(`layout/${LayoutMutationTypes.SHOW_GOODBYE}`, null, { root: true })
    await sleep(1000)
    commit(AppMutationTypes.RESET_IS_USER_ADMIN)
    commit(AppMutationTypes.STOP_LOADING)

    draftStorage.clear()
    await sessionApi.logout()
    window.location.replace('/login')
  },
  setCurrentUser({ commit }, user: UserInterface) {
    commit(AppMutationTypes.SET_CURRENT_USER, user)
  }
}

const appModule: VuexModuleInterface<AppState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions
}

export default appModule
